import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiSave } from 'react-icons/fi';

import * as S from './styles';
import SimpleInput from 'components/SimpleInput';
import Load from 'components/Load';
import TitleNavBarContext from 'context/TitleNavBarContext';
import SystemNotifyContext from 'context/SystemNotifyContext';
import { UseTranslationProps } from 'types/UseTranslationProps';
import { ContactDataProps, ContactFormField } from 'types/ContactProps';
import {
	formatPhoneNumber,
	formatWhatsappNumber,
	validateContactForm,
	ContactValidationResult
} from './utils';
import {
	getContactDataReq,
	saveContactDataReq,
	initialContactData
} from './reqs';

export default function Contact() {
	const { i18n }: UseTranslationProps = useTranslation();
	const { setDataAbouTitle } = useContext(TitleNavBarContext);
	const { handleNotify } = useContext(SystemNotifyContext);

	const [formData, setFormData] =
		useState<ContactDataProps>(initialContactData);
	const [formErrors, setFormErrors] = useState<
		ContactValidationResult['errors']
	>({});
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);

	// Atualiza o título e subtítulo na barra superior
	useLayoutEffect(() => {
		setDataAbouTitle({
			title: 'Canais de Atendimento',
			subtitle:
				'Defina as informações de contato disponibilizadas aos clientes no aplicativo.'
		});
	}, [i18n?.language, setDataAbouTitle]);

	// Carrega os dados iniciais
	useEffect(() => {
		let isMounted = true;

		(async () => {
			setIsLoading(true);
			const { isSuccess, data } = await getContactDataReq();
			if (isMounted) {
				if (isSuccess && data) {
					setFormData({
						id: data.id,
						phone: formatPhoneNumber(data.phone || ''),
						whatsapp: formatWhatsappNumber(data.whatsapp || ''),
						email: data.email || ''
					});
				}
				setIsLoading(false);
			}
		})();

		return () => {
			isMounted = false;
		};
	}, []);

	function handleFieldChange(field: ContactFormField, value: string) {
		let formattedValue = value;

		if (field === 'phone') {
			formattedValue = formatPhoneNumber(value);
		} else if (field === 'whatsapp') {
			formattedValue = formatWhatsappNumber(value);
		}

		setFormData((prev) => ({
			...prev,
			[field]: formattedValue
		}));

		// Limpa o erro específico do campo ao ser alterado
		if (formErrors[field]) {
			setFormErrors((prev) => {
				const nextErrors = { ...prev };
				delete nextErrors[field];
				return nextErrors;
			});
		}
	}

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();

		const validation = validateContactForm(formData);

		if (!validation.isValid) {
			setFormErrors(validation.errors);
			handleNotify(
				'Por favor, corrija os campos com dados inválidos antes de salvar.',
				'error'
			);
			return;
		}

		setIsSaving(true);
		const { isSuccess, data: savedData } = await saveContactDataReq(formData);
		setIsSaving(false);

		if (isSuccess) {
			if (savedData?.id) {
				setFormData((prev) => ({
					...prev,
					id: savedData.id
				}));
			}
			handleNotify('Dados de contato salvos com sucesso!', 'success');
		} else {
			handleNotify(
				'Erro ao salvar os dados de contato. Tente novamente.',
				'error'
			);
		}
	}

	return (
		<S.container>
			{isLoading ? (
				<S.loadingContainer>
					<Load showText text="Carregando informações de contato..." />
				</S.loadingContainer>
			) : (
				<S.card>
					<S.form noValidate onSubmit={handleSubmit}>
						<S.fieldWrapper>
							<SimpleInput
								label="Telefone"
								placeHolder="(00) 0000-0000 / 0800-000-0000"
								initialValue={formData.phone}
								getValue={(value: string) => handleFieldChange('phone', value)}
								error={!!formErrors.phone}
								messagemError={formErrors.phone || ''}
								maxLength={15}
							/>
							{!formErrors.phone && (
								<S.fieldHint>
									Telefone fixo, celular ou 0800 para suporte.
								</S.fieldHint>
							)}
						</S.fieldWrapper>

						<S.fieldWrapper>
							<SimpleInput
								label="WhatsApp"
								placeHolder="(00) 00000-0000"
								initialValue={formData.whatsapp}
								getValue={(value: string) =>
									handleFieldChange('whatsapp', value)
								}
								error={!!formErrors.whatsapp}
								messagemError={formErrors.whatsapp || ''}
								maxLength={15}
							/>
							{!formErrors.whatsapp && (
								<S.fieldHint>
									Número de WhatsApp para atendimento direto.
								</S.fieldHint>
							)}
						</S.fieldWrapper>

						<S.fieldWrapper>
							<SimpleInput
								label="E-mail"
								placeHolder="contato@exemplo.com"
								typeInput="email"
								initialValue={formData.email}
								getValue={(value: string) => handleFieldChange('email', value)}
								error={!!formErrors.email}
								messagemError={formErrors.email || ''}
								maxLength={120}
							/>
							{!formErrors.email && (
								<S.fieldHint>
									E-mail oficial para recebimento de solicitações.
								</S.fieldHint>
							)}
						</S.fieldWrapper>

						<S.actions>
							<S.saveButton
								type="submit"
								disabled={isSaving}
								$isLoading={isSaving}
							>
								<FiSave size={16} />
								{isSaving ? 'Salvando...' : 'Salvar alterações'}
							</S.saveButton>
						</S.actions>
					</S.form>
				</S.card>
			)}
		</S.container>
	);
}
